<script setup lang="ts">
/**
 * AI Assistant Component with MCP (Model Context Protocol) Support
 *
 * This component can display structured agent search and thinking states.
 *
 * Usage Example:
 *
 * // Option 1: Pass structured MCP data directly
 * const message = {
 *   id: '1',
 *   type: 'assistant',
 *   content: 'Thu thập nội dung chi tiết liên quan tới unit 1',
 *   timestamp: new Date(),
 *   mcpSections: [
 *     {
 *       type: 'search',
 *       title: 'Tìm kiếm',
 *       items: [
 *         { type: 'search', name: 'Tiếng anh 3 - Tập một - Global success', link: 'path/to/file', status: 'completed' },
 *         { type: 'search', name: 'Tiếng anh 3 - Sách giáo viên', link: 'path/to/file', status: 'completed' }
 *       ]
 *     },
 *     {
 *       type: 'write',
 *       title: 'Viết',
 *       items: [
 *         { type: 'write', name: 'Viết mục Aims', status: 'completed' },
 *         { type: 'write', name: 'Viết mục Knowledge', status: 'in-progress' }
 *       ]
 *     }
 *   ]
 * }
 *
 * // Option 2: Use plain text (component will try to parse MCP patterns)
 * const message = {
 *   id: '1',
 *   type: 'assistant',
 *   content: `
 *     Tìm kiếm:
 *     - Tiếng anh 3 - Tập một - Global success
 *     - Sách giáo viên
 *
 *     Viết:
 *     - Viết mục Aims
 *     - Viết mục Knowledge
 *   `,
 *   timestamp: new Date()
 * }
 */
import { computed, nextTick, ref, ref as vueRef, watch } from 'vue'
import { NButton, NInput, NSpace, NText, NTooltip } from 'naive-ui'
import type { AiAssistantProps, McpSearchItem, McpSection } from './types'
import { SvgIcon } from '@/components/common'
import PreviewFile from '@/components/common/PreviewFile/index.vue'
import { useTheme } from '@/hooks/useTheme'
import { useBasicLayout } from '@/hooks/useBasicLayout'
import { t } from '@/locales'

const props = withDefaults(
  defineProps<
		AiAssistantProps & {
		  inputValue?: string
		  context?: string
		  contextAttachment?: { name: string; url: string }[]
		}
	>(),
  {
    title: 'AI Assistant',
    placeholder: 'Type your message...',
    greeting: 'Hello! How can I help you today?',
    description: 'Ask me anything and I\'ll do my best to assist you.',
    autoScroll: true,
    maxMessages: 100,
    showTimestamp: false,
    showConversationInfo: false,
    theme: 'light',
    disabled: false,
    loading: false,
    messages: () => [],
    inputValue: '',
    context: '',
    contextAttachment: () => [],
  },
)

const emit = defineEmits<{
  send: [userMessage: string]
  close: []
  'update:inputValue': [value: string]
  clearContext: []
}>()

const { themeOverrides } = useTheme()
const { isIPad } = useBasicLayout()

const primaryColor = computed(
  () => themeOverrides.value?.common?.primaryColor || '#764ab9',
)

const primaryColorHover = computed(
  () => themeOverrides.value?.common?.primaryColorHover || '#9a76e4',
)

// Reactive state - only UI state
const assistantInput = ref(props.inputValue)
const messagesContainer = ref<HTMLElement>()
const isContextExpanded = ref(false)
const expandedContext = ref<Record<number, boolean>>({})
const inputRef = vueRef()

// Preview file state
const showPreviewFile = ref(false)
const previewFilePath = ref<string | null>(null)
const previewFileId = ref<string | null>(null)

function toggleContextExpandBubble(idx: number) {
  expandedContext.value[idx] = !expandedContext.value[idx]
}

// Watch for prop inputValue changes
watch(
  () => props.inputValue,
  (val) => {
    if (typeof val === 'string' && val.trim()) {
      assistantInput.value = val
      // Auto send the message if it's from external source (toolbar buttons)
      if (
        val === 'Giải thích nội dung này'
				|| val === 'Phân tích và tóm tắt nội dung này'
      ) {
        nextTick(() => {
          handleSend()
        })
      }
    }
  },
)

// Watch for messages changes and auto scroll
watch(
  () => props.messages,
  () => {
    if (props.autoScroll) {
      nextTick(() => {
        scrollToBottom()
      })
    }
  },
  { deep: true },
)

// Computed
const canSend = computed(() => {
  return assistantInput.value.trim() && !props.loading && !props.disabled
})

const messageCount = computed(() => props.messages?.length || 0)

// Check if context text is long enough to need collapse
const isContextLong = computed(() => {
  return props.context && props.context.length > 150
})

// Get truncated context text
const truncatedContext = computed(() => {
  if (!props.context)
    return ''
  if (!isContextLong.value)
    return props.context
  return isContextExpanded.value
    ? props.context
    : `${props.context.substring(0, 100)}...`
})

// Methods - Pure UI methods only
function handleSend() {
  const text = assistantInput.value.trim()
  if (!canSend.value)
    return

  // Simply emit the user message - parent handles building full payload
  emit('send', text)

  // Clear input only - context clearing is handled by parent
  assistantInput.value = ''
}

function handleClose() {
  emit('close')
}

function scrollToBottom() {
  if (props.autoScroll && messagesContainer.value)
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
}

function clearContext() {
  isContextExpanded.value = false
  emit('clearContext')
}

function toggleContextExpand() {
  isContextExpanded.value = !isContextExpanded.value
}

// Keyboard handlers
function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    handleSend()
  }
}

function handleInput(val: string) {
  assistantInput.value = val
  emit('update:inputValue', val)
}

// Handle click on MCP search item
function handleSearchItemClick(item: McpSearchItem) {
  if (!item.link || item.status === 'in-progress')
    return

  // Always show preview modal, treat link as file path
  // PreviewFile component will handle both file paths and URLs via downloadFile API
  previewFilePath.value = item.link
  previewFileId.value = null
  showPreviewFile.value = true
}

function handleClosePreview() {
  showPreviewFile.value = false
  previewFilePath.value = null
  previewFileId.value = null
}

// Expose methods for parent components
defineExpose({
  focusInput: () => {
    inputRef.value?.focus && inputRef.value.focus()
  },
  setInputValue: (value: string) => {
    assistantInput.value = value
    emit('update:inputValue', value)
  },
})

function hexToRgb(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `${r},${g},${b}`
}

// Parse MCP-style structured content from message
function parseMcpContent(
  content: string,
): { sections: McpSection[]; plainText: string } | null {
  // Try to detect MCP-style patterns in content
  // Pattern 1: Tìm kiếm / Search section
  const searchPattern
		= /(?:Tìm kiếm|Thu thập nội dung chi tiết liên quan tới unit \d+)[\s\S]*?(?=Viết|$)/i
  // Pattern 2: Viết / Write section
  const writePattern = /Viết[\s\S]*?(?=\n\n|$)/i

  const hasSearchSection = searchPattern.test(content)
  const hasWriteSection = writePattern.test(content)

  if (!hasSearchSection && !hasWriteSection)
    return null // Not MCP format, return null

  const sections: McpSection[] = []
  let remainingContent = content

  // Extract search section
  const searchMatch = content.match(searchPattern)
  if (searchMatch) {
    const searchText = searchMatch[0]
    const items: McpSearchItem[] = []

    // Extract search items - looking for patterns like "- Name" or "Name - Link"
    const itemPattern = /[-•]\s*(.+?)(?:\s*-\s*(.+?))?$/gm
    let itemMatch
    while ((itemMatch = itemPattern.exec(searchText)) !== null) {
      const name = itemMatch[1].trim()
      const link = itemMatch[2]?.trim()
      if (name && name !== 'Tìm kiếm' && !name.includes('Thu thập')) {
        items.push({
          type: 'search',
          name,
          link,
          status: 'completed',
        })
      }
    }

    if (items.length > 0) {
      sections.push({
        type: 'search',
        title: 'Tìm kiếm',
        items,
      })
    }
    remainingContent = remainingContent.replace(searchText, '')
  }

  // Extract write section
  const writeMatch = remainingContent.match(writePattern)
  if (writeMatch) {
    const writeText = writeMatch[0]
    const items: McpSearchItem[] = []

    // Extract write items
    const itemPattern = /[-•]\s*Viết\s+mục\s+(.+?)$/gm
    let itemMatch
    while ((itemMatch = itemPattern.exec(writeText)) !== null) {
      const name = itemMatch[1].trim()
      items.push({
        type: 'write',
        name: `Viết mục ${name}`,
        status: 'completed',
      })
    }

    if (items.length > 0) {
      sections.push({
        type: 'write',
        title: 'Viết',
        items,
      })
    }
    remainingContent = remainingContent.replace(writeText, '')
  }

  // Get plain text (text that's not part of sections)
  const plainText = remainingContent.trim()

  return { sections, plainText }
}

function formatMessageContent(content: string) {
  if (!content)
    return ''

  // Clean up whitespace first
  let formatted = content
    .trim() // Remove leading/trailing whitespace
    .replace(/\n{3,}/g, '\n\n') // Replace 3+ consecutive newlines with 2
    .replace(/[ \t]+$/gm, '') // Remove trailing spaces on each line

  // Escape HTML to prevent XSS
  formatted = formatted
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

  // Handle markdown-style formatting BEFORE converting line breaks
  // Headers: ### text (xử lý trước khi thay \n)
  formatted = formatted.replace(
    /^\s*### (.*$)/gm,
    '<h3 class="text-base font-semibold mt-4 mb-2">$1</h3>',
  )
  formatted = formatted.replace(
    /^\s*## (.*$)/gm,
    '<h2 class="text-lg font-semibold mt-5 mb-3">$1</h2>',
  )
  formatted = formatted.replace(
    /^\s*# (.*$)/gm,
    '<h1 class="text-xl font-semibold mt-6 mb-4">$1</h1>',
  )

  // Bold: **text** or __text__ (xử lý trước lists để tránh conflict)
  formatted = formatted.replace(
    /\*\*(.*?)\*\*/g,
    '<strong class="font-semibold">$1</strong>',
  )
  formatted = formatted.replace(
    /__(.*?)__/g,
    '<strong class="font-semibold">$1</strong>',
  )

  // Italic: *text* or _text_ (cần cẩn thận với conflict với bold)
  formatted = formatted.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
  formatted = formatted.replace(/(?<!_)_([^_]+)_(?!_)/g, '<em>$1</em>')

  // Code: `text`
  formatted = formatted.replace(
    /`(.*?)`/g,
    '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>',
  )

  // Links: [text](url)
  formatted = formatted.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" class="text-blue-600 hover:text-blue-800 underline">$1</a>',
  )

  // Lists: Handle both unordered (-) and ordered (1.) lists with proper indentation
  // First, mark list items with temporary markers to preserve them during newline conversion
  formatted = formatted.replace(
    /^(\s*)[-*+]\s+(.*)$/gm,
    (match, indent, content) => {
      const level = Math.floor(indent.length / 2) // 2 spaces per level
      return `<li data-level="${level}" class="list-item-unordered">${content}</li>`
    },
  )

  formatted = formatted.replace(
    /^(\s*)(\d+)\.\s+(.*)$/gm,
    (match, indent, number, content) => {
      const level = Math.floor(indent.length / 2)
      return `<li data-level="${level}" class="list-item-ordered">${content}</li>`
    },
  )

  // Handle line breaks AFTER processing headers and lists
  formatted = formatted
    .replace(/\n{2,}/g, '<br><br>') // Double+ newlines become double br
    .replace(/\n/g, '<br>') // Single newlines become single br

  // Group consecutive list items into proper ul/ol structures
  // Handle unordered lists
  formatted = formatted.replace(
    /(<li[^>]*class="[^"]*list-item-unordered[^"]*"[^>]*>.*?<\/li>(<br>|$))+/g,
    (match) => {
      const listItems = match.replace(/(<br>)+/g, '').trim()
      return `<ul class="ml-6 my-3 space-y-1" style="list-style-type: disc; padding-left: 1.5rem;">${listItems}</ul>`
    },
  )

  // Handle ordered lists
  formatted = formatted.replace(
    /(<li[^>]*class="[^"]*list-item-ordered[^"]*"[^>]*>.*?<\/li>(<br>|$))+/g,
    (match) => {
      const listItems = match.replace(/(<br>)+/g, '').trim()
      return `<ol class="ml-6 my-3 space-y-1" style="list-style-type: decimal; padding-left: 1.5rem;">${listItems}</ol>`
    },
  )

  // Clean up list item classes and add proper styling
  formatted = formatted.replace(
    /<li[^>]*class="[^"]*list-item-(un)?ordered[^"]*"[^>]*>/g,
    '<li class="leading-relaxed" style="display: list-item;">',
  )

  // Clean up extra spacing and formatting
  formatted = formatted
    .replace(/(<br>\s*){3,}/g, '<br><br>') // Remove excessive br tags
    .replace(/(<\/h[1-6]>)\s*<br>/g, '$1') // Remove br after headers
    .replace(/(<\/ul>)\s*<br>/g, '$1') // Remove br after unordered lists
    .replace(/(<\/ol>)\s*<br>/g, '$1') // Remove br after ordered lists
    .replace(/(<ul[^>]*>)\s*<br>/g, '$1') // Remove br after opening ul tags
    .replace(/(<ol[^>]*>)\s*<br>/g, '$1') // Remove br after opening ol tags

  return formatted
}
</script>

<template>
  <div class="ai-assistant flex flex-col h-full">
    <!-- Header -->
    <div class="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800">
      <div class="flex items-center justify-between">
        <div class="flex flex-col">
          <NText class="font-medium" strong>
            {{ title }}
          </NText>
          <NText v-if="showConversationInfo" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {{ messageCount }} messages
          </NText>
        </div>
        <NButton size="small" quaternary circle @click="handleClose">
          <template #icon>
            <SvgIcon icon="ion:close-outline" size="16" />
          </template>
        </NButton>
      </div>
    </div>

    <!-- Messages Area -->
    <div
      ref="messagesContainer"
      class="flex-1 overflow-y-auto px-4 py-6 space-y-4 min-h-0 bg-white dark:bg-gray-800"
    >
      <!-- Empty state -->
      <div v-if="messageCount === 0" class="text-center py-8">
        <div
          class="w-16 h-16 mx-auto mb-4 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center"
        >
          <SvgIcon icon="ion:sparkles-outline" size="24" class="text-blue-600 dark:text-blue-400" />
        </div>
        <NText depth="2" class="text-sm text-gray-700 dark:text-gray-300">
          {{ greeting }}
        </NText>
        <NText depth="3" class="text-xs mt-1 block text-gray-500 dark:text-gray-400">
          {{ description }}
        </NText>
      </div>

      <!-- Messages -->
      <div v-for="(message, idx) in props.messages" :key="message.id || idx" class="mb-4">
        <div v-if="message.type === 'user'" class="flex flex-col items-end">
          <div v-if="message.subtitle" class="w-full max-w-sm mb-1 text-right">
            <div
              class="inline-block bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-700 dark:text-gray-300 relative cursor-pointer transition-all duration-200 context-bubble"
              :class="{
                collapsed:
                  !expandedContext[idx] && message.subtitle.length > 100,
              }" style="text-align: left; min-width: 120px; max-width: 100%" @click="toggleContextExpandBubble(idx)"
            >
              <span class="absolute left-2 top-2 text-gray-400">
                <svg
                  v-if="message.subtitle.length > 100" :style="{
                    transform: expandedContext[idx]
                      ? 'rotate(90deg)'
                      : 'rotate(0deg)',
                  }" width="16" height="16" fill="none" viewBox="0 0 16 16"
                >
                  <path
                    d="M6 12l4-4-4-4" stroke="currentColor" stroke-width="1.5"
                    stroke-linecap="round" stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="ml-5">
                {{
                  expandedContext[idx] || message.subtitle.length <= 100 ? message.subtitle
                  : `${message.subtitle.slice(0, 100)}...` }} </span>
            </div>
          </div>
          <div v-if="message.attachment && message.attachment.length" class="mb-1 w-full max-w-sm text-right">
            <div class="bg-blue-50 dark:bg-blue-900/30 rounded px-2 py-1 inline-block">
              <span class="text-xs font-medium text-blue-600 dark:text-blue-400">Attachments:</span>
              <div
                v-for="(file, fidx) in message.attachment" :key="fidx"
                class="text-xs text-blue-500 dark:text-blue-400"
              >
                <a
                  :href="file.url" target="_blank"
                  class="underline hover:text-blue-700 dark:hover:text-blue-300"
                >{{ file.name }}</a>
              </div>
            </div>
          </div>
          <div class="max-w-sm rounded-2xl px-4 py-3 user-bubble" :style="{ backgroundColor: primaryColor }">
            <NText class="text-sm leading-relaxed text-white" style="color: #fff">
              {{ message.content }}
            </NText>
            <NText v-if="showTimestamp && message.timestamp" class="text-xs text-white/70 mt-1 block">
              {{ message.timestamp.toLocaleTimeString() }}
            </NText>
          </div>
        </div>
        <div v-else class="flex justify-start">
          <div
            class="assistant-bubble px-0 py-0" :class="message.mcpSections
              && message.mcpSections.some((s) => s.type === 'write')
              ? 'mcp-write-full-width'
              : 'max-w-2xl'
            "
          >
            <!-- AI Response Header -->
            <div class="flex items-center gap-2 mb-2">
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center shadow-sm" :style="{
                  background: `linear-gradient(135deg, ${primaryColor}, ${primaryColorHover})`,
                  boxShadow: `0 2px 6px rgba(${hexToRgb(primaryColor)}, 0.3)`,
                }" :class="{
                  'animate-pulse':
                    props.loading && idx === props.messages.length - 1,
                }"
              >
                <img src="@/assets/svg/ai-thinking.svg" alt="AI Thinking" class="w-4 h-4">
              </div>
              <NText class="text-md font-medium" :style="{ color: primaryColor }">
                <span v-if="props.loading && idx === props.messages.length - 1" class="typing-text">
                  {{ t("common.aiThinking") }}
                  <span class="loading-dots">
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </span>
                </span>
                <span v-else>
                  {{ t("common.aiResponse") }}
                </span>
              </NText>
            </div>

            <!-- Check if content has MCP structure -->
            <template
              v-if="
                (message.mcpSections && message.mcpSections.length > 0)
                  || (() => {
                    const mcpParsed = parseMcpContent(message.content);
                    if (mcpParsed && mcpParsed.sections.length > 0) {
                      return true;
                    }
                    return false;
                  })()
              "
            >
              <!-- Render MCP Structured Content -->
              <div class="mcp-container">
                <!-- Plain text before sections -->
                <div
                  v-if="message.content && !message.mcpSections"
                  class="text-sm leading-relaxed text-gray-700 dark:text-gray-300 mb-3" v-html="formatMessageContent(
                    parseMcpContent(message.content)?.plainText || '',
                  )
                  "
                />
                <div
                  v-else-if="message.content && message.mcpSections"
                  class="text-sm leading-relaxed text-gray-700 dark:text-gray-300 mb-3"
                  v-html="formatMessageContent(message.content)"
                />

                <!-- MCP Sections -->
                <div
                  v-for="(section, sIdx) in message.mcpSections
                    || parseMcpContent(message.content)?.sections
                    || []" :key="sIdx" class="mcp-section mb-4"
                >
                  <!-- Section Header -->
                  <div
                    v-if="section.title"
                    class="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700"
                  >
                    <div class="w-5 h-5 flex items-center justify-center">
                      <SvgIcon
                        v-if="section.type === 'search'" icon="ion:search-outline"
                        size="18"
                      />
                      <SvgIcon
                        v-else-if="section.type === 'write'" icon="ion:create-outline"
                        size="18"
                      />
                      <SvgIcon
                        v-else-if="section.type === 'thinking'" icon="ion:bulb-outline"
                        size="18"
                      />
                    </div>
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {{ section.title }}
                    </span>
                  </div>

                  <!-- Section Items -->
                  <div v-if="section.items && section.items.length" class="space-y-2">
                    <div
                      v-for="(item, iIdx) in section.items" :key="iIdx"
                      class="mcp-item mcp-item-with-divider flex items-center px-3 py-2 rounded-lg transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                      :class="{
                        'mcp-item-loading': item.status === 'in-progress',
                        'mcp-item-clickable':
                          section.type === 'search'
                          && item.link
                          && item.status === 'completed',
                      }" @click="
                        section.type === 'search'
                          && item.link
                          && item.status === 'completed'
                          ? handleSearchItemClick(item)
                          : undefined
                      "
                    >
                      <!-- Left Section: Icon + Label -->
                      <div class="flex items-center gap-2 flex-shrink-0">
                        <!-- Status Icon (only for non-search items) -->
                        <div
                          v-if="section.type !== 'search'"
                          class="flex-shrink-0 w-4 h-4 flex items-center justify-center"
                        >
                          <SvgIcon
                            v-if="item.status === 'completed'"
                            icon="ion:checkmark-circle-outline" size="16"
                            class="text-green-600 dark:text-green-400"
                          />
                          <div
                            v-else-if="item.status === 'in-progress'"
                            class="mcp-loading-spinner"
                          >
                            <div class="mcp-spinner-dot" />
                          </div>
                          <SvgIcon
                            v-else icon="ion:ellipse-outline" size="16"
                            class="text-gray-400 dark:text-gray-600"
                          />
                        </div>
                        <!-- Section Icon + Label -->
                        <div class="flex items-center gap-2">
                          <SvgIcon
                            v-if="section.type === 'search'" icon="ion:search-outline"
                            size="16" class="text-gray-500 dark:text-gray-400"
                          />
                          <SvgIcon
                            v-else-if="section.type === 'write'"
                            icon="ion:create-outline" size="16"
                            class="text-gray-500 dark:text-gray-400"
                          />
                          <SvgIcon
                            v-else-if="section.type === 'thinking'"
                            icon="ion:bulb-outline" size="16"
                            class="text-gray-500 dark:text-gray-400"
                          />
                          <span class="text-sm text-gray-500 dark:text-gray-400">
                            {{
                              section.title
                                || t("lessonPlanGenerator.mcp.searchTitle")
                            }}
                          </span>
                        </div>
                      </div>

                      <!-- Divider -->
                      <div class="mcp-item-divider mx-3" />

                      <!-- Right Section: File Name -->
                      <div class="flex-1 min-w-0 flex items-center gap-2">
                        <NTooltip v-if="section.type === 'search'" trigger="hover">
                          <template #trigger>
                            <span
                              class="text-sm text-gray-700 dark:text-gray-300 truncate block max-w-[200px]"
                              :class="{
                                'cursor-pointer':
                                  item.link && item.status === 'completed',
                              }"
                            >
                              {{ item.name }}
                            </span>
                          </template>
                          {{ item.name }}
                        </NTooltip>
                        <span
                          v-else class="text-sm text-gray-700 dark:text-gray-300 truncate"
                          :class="{
                            'mcp-text-loading': item.status === 'in-progress',
                          }"
                        >
                          {{ item.name }}
                        </span>
                        <SvgIcon
                          v-if="
                            section.type === 'search'
                              && item.link
                              && item.status === 'completed'
                          " icon="ion:open-outline" size="14" class="text-gray-400 dark:text-gray-500 flex-shrink-0"
                        />
                      </div>
                    </div>
                    <div
                      v-if="section.type === 'search'"
                      class="text-sm leading-relaxed text-gray-700 dark:text-gray-300"
                    >
                      {{ t("lessonPlanGenerator.messages.researchContent") }}
                    </div>
                  </div>

                  <!-- Section Content (for thinking type) -->
                  <div
                    v-if="section.content"
                    class="text-sm leading-relaxed text-gray-700 dark:text-gray-300 px-3 py-2"
                    v-html="formatMessageContent(section.content)"
                  />
                </div>
              </div>
            </template>

            <!-- Regular formatted content -->
            <div
              v-else
              class="text-sm leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-base"
              v-html="formatMessageContent(message.content)"
            />

            <!-- Typing dot right after the content -->
            <span v-if="props.loading && idx === props.messages.length - 1" class="typing-dot">
              <span class="dot-pulse" />
            </span>
            <NText
              v-if="showTimestamp && message.timestamp"
              class="text-xs text-gray-500 dark:text-gray-400 mt-1 block"
            >
              {{ message.timestamp.toLocaleTimeString() }}
            </NText>
          </div>
        </div>
      </div>
    </div>

    <!-- Input Area -->

    <div class="assistant-input-area px-4 py-4 flex-shrink-0 bg-white dark:bg-gray-800" :class="{ 'ipad-py-2': isIPad }">
      <!-- Before Input Slot -->
      <slot name="before-input" />

      <!-- Modern Input Container -->
      <div
        class="w-full max-w-[900px] m-auto border rounded-[15px] p-[10px] transition-all duration-200 relative border-[#80808082] dark:border-gray-600 bg-white dark:bg-gray-800"
        :class="{
          'border-blue-500 border-2 border-dashed bg-blue-50 dark:bg-blue-900/20': false,
        }"
      >
        <!-- Context Label -->
        <div
          v-if="
            props.context
              || (props.contextAttachment && props.contextAttachment.length)
          " class="mb-3"
        >
          <div class="context-content">
            <!-- Selected Text -->
            <div v-if="props.context" class="context-text-section">
              <div class="context-quote-container dark:bg-gray-800 dark:border-gray-700">
                <div class="context-quote-text dark:text-gray-300">
                  ❝ {{ truncatedContext }}
                  <a v-if="isContextLong" class="context-expand-link" @click="toggleContextExpand">
                    {{ isContextExpanded ? "Thu gọn" : "Xem thêm" }}
                  </a>
                </div>
                <button
                  class="context-close-btn dark:text-gray-300 dark:hover:text-gray-100"
                  title="Clear context" @click="clearContext"
                >
                  <SvgIcon icon="ion:close-outline" size="16" />
                </button>
              </div>
            </div>

            <!-- File Attachments -->
            <div
              v-if="props.contextAttachment && props.contextAttachment.length"
              class="context-files-section"
            >
              <NSpace :size="10" class="flex-wrap">
                <div
                  v-for="(file, index) in props.contextAttachment" :key="file.url"
                  class="context-file-item" :style="{ animationDelay: `${index * 100}ms` }"
                >
                  <div class="context-file-display">
                    <div class="context-file-icon">
                      <SvgIcon icon="ion:document-outline" class="w-6 h-6 text-gray-500" />
                    </div>
                    <div class="context-file-info">
                      <div class="context-file-name" :title="file.name">
                        {{ file.name }}
                      </div>
                      <div class="context-file-type">
                        File
                      </div>
                    </div>
                  </div>
                </div>
              </NSpace>
            </div>
          </div>
        </div>

        <div class="flex flex-col items-center justify-between">
          <NInput
            ref="inputRef" v-model:value="assistantInput" :placeholder="placeholder" :bordered="false"
            type="textarea" :autosize="{ minRows: 1, maxRows: 6 }" class="w-full" spellcheck="false"
            :disabled="disabled" @keydown="handleKeydown" @update:value="handleInput"
          />
        </div>

        <div class="flex justify-between align-center w-full">
          <div class="flex items-center gap-2">
            <!-- File Upload Button (placeholder for future use) -->
            <div class="text-2xl hidden text-gray-400 p-2 cursor-not-allowed rounded-full">
              <SvgIcon icon="ion:add-outline" size="22" />
            </div>

            <!-- Additional action buttons can be added here -->
          </div>

          <div class="flex items-center gap-2">
            <!-- Voice button (placeholder) -->
            <div class="text-2xl text-gray-400 dark:text-gray-500 p-2 cursor-not-allowed rounded-full">
              <SvgIcon icon="ion:mic-outline" size="22" />
            </div>

            <!-- Send button -->
            <div
              class="text-2xl p-2 transition-all duration-200 rounded-full" :class="[
                !canSend
                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'text-primary cursor-pointer hover:bg-primary hover:text-white',
              ]" @click="canSend ? handleSend() : null"
            >
              <SvgIcon
                :icon="props.loading ? 'ri:loader-4-line' : 'ri:send-plane-fill'
                " :class="{ 'animate-spin': props.loading }" size="22"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview File Modal -->
    <PreviewFile
      v-if="previewFilePath || previewFileId" :file-path="previewFilePath || undefined"
      :file-id="previewFileId || undefined" :is-show-preview-file="showPreviewFile"
      @handle-close="handleClosePreview"
    />
  </div>
</template>

<style scoped>
.ai-assistant {
	height: 100%;
}

.assistant-input-area {
	padding-bottom: calc(1rem + env(safe-area-inset-bottom));
}

.ipad-py-2 {
	padding-top: 0.5rem !important;
	padding-bottom: 0.5rem !important;
}

.user-bubble {
	color: #fff;
}

.assistant-bubble {
	background: none;
	border: none;
	box-shadow: none;
	color: inherit;
	padding: 0;
}

.flex-1 {
	flex: 1 1 0%;
	min-height: 0;
}

.flex-shrink-0 {
	flex-shrink: 0;
}

.overflow-y-auto {
	overflow-y: auto;
	overscroll-behavior: contain;
}

/* New styles for context label */
.context-card {
	border-radius: 12px;
	overflow: hidden;
	transition: all 0.3s ease;
}

.context-card:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.context-clear-btn {
	transition: all 0.2s ease;
}

.context-clear-btn:hover {
	transform: scale(1.1);
}

.context-content {
	padding: 0;
}

.context-text-section {
	margin-bottom: 12px;
}

.context-quote-container {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	background: #f5f5f5;
	border: 1px solid #e5e5e5;
	border-radius: 12px;
	padding: 12px 16px;
	transition: all 0.2s ease;
	position: relative;
	max-height: 200px;
	overflow-y: auto;
}

.dark .context-quote-container {
	background: #374151;
	border-color: #4b5563;
}

.context-quote-text {
	flex: 1;
	font-size: 0.875rem;
	line-height: 1.5;
	color: #666;
	font-style: normal;
	margin-right: 8px;
}

.dark .context-quote-text {
	color: #d1d5db;
}

.context-close-btn {
	position: absolute;
	top: 8px;
	right: 8px;
	background: none;
	border: none;
	color: #333;
	cursor: pointer;
	padding: 4px;
	border-radius: 4px;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10;
}

.context-close-btn:hover {
	background: rgba(0, 0, 0, 0.1);
	color: #000;
}

.dark .context-close-btn:hover {
	background: rgba(255, 255, 255, 0.1);
	color: #fff;
}

.context-expand-link {
	margin-left: 4px;
	color: v-bind(primaryColor);
	text-decoration: none;
	font-weight: 500;
	cursor: pointer;
	transition: color 0.2s ease;
	font-style: normal;
}

.context-expand-link:hover {
	color: v-bind(primaryColorHover);
	text-decoration: underline;
}

.context-files-section {
	margin-top: 8px;
}

.context-file-item {
	animation: contextFileSlideIn 0.4s ease-out;
}

.context-file-display {
	display: flex;
	align-items: center;
	background: rgba(248, 250, 252, 0.8);
	border: 1px solid rgba(203, 213, 225, 0.6);
	border-radius: 8px;
	padding: 8px 12px;
	min-width: 120px;
	max-width: 200px;
	transition: all 0.3s ease;
}

.dark .context-file-display {
	background: rgba(55, 65, 81, 0.8);
	border-color: rgba(75, 85, 99, 0.6);
}

.context-file-display:hover {
	background: rgba(241, 245, 249, 0.9);
	border-color: rgba(148, 163, 184, 0.8);
	transform: translateY(-1px);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.dark .context-file-display:hover {
	background: rgba(55, 65, 81, 0.9);
	border-color: rgba(107, 114, 128, 0.8);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.context-file-icon {
	margin-right: 10px;
	flex-shrink: 0;
}

.context-file-info {
	display: flex;
	flex-direction: column;
	min-width: 0;
	flex: 1;
}

.context-file-name {
	font-size: 12px;
	font-weight: 500;
	color: #374151;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	line-height: 1.2;
	margin-bottom: 2px;
}

.dark .context-file-name {
	color: #e5e7eb;
}

.context-file-type {
	font-size: 10px;
	color: #6b7280;
	font-weight: 400;
	text-transform: uppercase;
	letter-spacing: 0.025em;
}

.dark .context-file-type {
	color: #9ca3af;
}

@keyframes contextFileSlideIn {
	from {
		opacity: 0;
		transform: translateY(10px) scale(0.9);
	}

	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}

/* Modern input styling */
:deep(.n-input .n-input__textarea) {
	resize: none;
	border: none;
	outline: none;
	background: transparent;
	font-family: inherit;
}

:deep(.n-input .n-input__textarea-el) {
	border: none;
	outline: none;
	background: transparent;
	padding: 0;
	font-family: inherit;
}

:deep(.n-input) {
	border: none;
	background: transparent;
}

:deep(.n-input__border) {
	border: none;
}

:deep(.n-input__state-border) {
	border: none;
}

/* Primary color styling */
.text-primary {
	color: v-bind(primaryColor);
}

.hover\:bg-primary:hover {
	background-color: v-bind(primaryColor);
}

.hover\:text-white:hover {
	color: white;
}

/* Button hover effects */
.cursor-pointer:hover {
	/* Remove scale effect */
	transform: none !important;
}

.context-bubble:hover {
	/* Remove scale effect */
	transform: none !important;
	box-shadow: none;
}

/* Loading animation */
@keyframes spin {
	from {
		transform: rotate(0deg);
	}

	to {
		transform: rotate(360deg);
	}
}

.animate-spin {
	animation: spin 1s linear infinite;
}

/* Message formatting styles */
.assistant-bubble {
	word-wrap: break-word;
	overflow-wrap: break-word;
}

.assistant-bubble h1,
.assistant-bubble h2,
.assistant-bubble h3 {
	margin-top: 0.5rem;
	margin-bottom: 0.5rem;
	line-height: 1.4;
	font-weight: 500;
}

.assistant-bubble ul,
.assistant-bubble ol {
	margin: 0.5rem 0;
	padding-left: 1rem;
}

.assistant-bubble ul {
	list-style-type: disc;
}

.assistant-bubble ol {
	list-style-type: decimal;
	counter-reset: list-counter;
}

.assistant-bubble ol li {
	counter-increment: list-counter;
	list-style-type: decimal;
}

.assistant-bubble li {
	margin: 0.25rem 0;
	display: list-item;
}

/* Override any potential global CSS that might reset list styling */
.assistant-bubble ul li::before,
.assistant-bubble ol li::before {
	content: none !important;
}

.assistant-bubble ul li {
	list-style: disc outside;
}

.assistant-bubble ol li {
	list-style: decimal outside;
}

.assistant-bubble code {
	background-color: #f3f4f6;
	padding: 0.125rem 0.25rem;
	border-radius: 0.25rem;
	font-family: "Courier New", monospace;
	font-size: 0.875rem;
}

.dark .assistant-bubble code {
	background-color: #374151;
	color: #e5e7eb;
}

.assistant-bubble strong {
	font-weight: 500;
}

.assistant-bubble em {
	font-style: italic;
}

.assistant-bubble a {
	color: #2563eb;
	text-decoration: underline;
}

.dark .assistant-bubble a {
	color: #60a5fa;
}

.assistant-bubble a:hover {
	color: #1d4ed8;
}

.dark .assistant-bubble a:hover {
	color: #93c5fd;
}

.context-bubble {
	position: relative;
	padding-left: 28px;
	min-height: 32px;
	max-width: 100%;
	word-break: break-word;
}

.context-bubble.collapsed {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 320px;
}

.context-bubble svg {
	transition: transform 0.2s;
}

/* Loading text animation */
.typing-text {
	display: inline-flex;
	align-items: center;
}

.loading-dots {
	display: inline-flex;
	margin-left: 2px;
}

.loading-dots span {
	opacity: 0;
	animation: loadingDots 1.4s infinite ease-in-out;
}

.loading-dots span:nth-child(1) {
	animation-delay: 0s;
}

.loading-dots span:nth-child(2) {
	animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
	animation-delay: 0.4s;
}

@keyframes loadingDots {

	0%,
	80%,
	100% {
		opacity: 0;
	}

	40% {
		opacity: 1;
	}
}

/* Typing dot animation - single scaling dot after content */
.typing-dot {
	display: inline-block;
	margin-left: 2px;
	vertical-align: middle;
}

.dot-pulse {
	display: inline-block;
	width: 10px;
	height: 10px;
	background-color: v-bind(primaryColor);
	border-radius: 50%;
	animation: dotPulse 1.2s ease-in-out infinite;
}

@keyframes dotPulse {

	0%,
	100% {
		transform: scale(0.8);
		opacity: 0.5;
	}

	50% {
		transform: scale(1.2);
		opacity: 1;
	}
}

/* MCP Styles */
.mcp-container {
	width: 100%;
}

.mcp-section {
	background: transparent;
	border-radius: 8px;
}

.mcp-item {
	border: 1px solid transparent;
	cursor: default;
}

.mcp-item:hover {
	border-color: rgba(229, 231, 235, 0.8);
}

.dark .mcp-item:hover {
	border-color: rgba(75, 85, 99, 0.8);
}

.mcp-item-with-divider {
	background: #f9fafb;
	border: 1px solid #e5e7eb;
}

.dark .mcp-item-with-divider {
	background: #1f2937;
	border-color: #374151;
}

.mcp-item-divider {
	width: 1px;
	height: 20px;
	background: #e5e7eb;
	flex-shrink: 0;
}

.dark .mcp-item-divider {
	background: #4b5563;
}

/* Full width for MCP write sections */
.mcp-write-full-width {
	width: 100%;
	max-width: 100% !important;
}

/* MCP Loading Animation for Write Items */
.mcp-loading-spinner {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	position: relative;
}

.mcp-spinner-dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: #3b82f6;
	animation: mcpSpinnerPulse 1.4s ease-in-out infinite;
}

.dark .mcp-spinner-dot {
	background-color: #60a5fa;
}

@keyframes mcpSpinnerPulse {

	0%,
	100% {
		transform: scale(0.8);
		opacity: 0.5;
	}

	50% {
		transform: scale(1.2);
		opacity: 1;
	}
}

/* Loading animation for in-progress item */
.mcp-item-loading {
	animation: mcpItemPulse 2s ease-in-out infinite;
}

.mcp-item-clickable {
	cursor: pointer;
}

@keyframes mcpItemPulse {

	0%,
	100% {
		opacity: 1;
	}

	50% {
		opacity: 0.85;
	}
}

/* Loading animation for text when in-progress */
.mcp-text-loading {
	animation: mcpTextPulse 1.2s ease-in-out infinite;
	color: #3b82f6 !important;
}

.dark .mcp-text-loading {
	color: #60a5fa !important;
}

@keyframes mcpTextPulse {

	0%,
	100% {
		opacity: 1;
	}

	50% {
		opacity: 0.5;
	}
}

/* Adjust max-width for assistant messages to accommodate MCP content */
.assistant-bubble {
	max-width: 600px;
}
</style>
